import { useMemo, useContext, useCallback } from 'react';
import { queryify } from '@bingads-webui/url-util';
import { odata } from '@bingads-webui/http-util';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { publisherQueries } from '@bingads-webui-campaign-react/merchant-shared';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { platforms } from './constants';

const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
        hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
  };

  return oDataConfig;
};

const attachIdsToResults = results => results.map(result => ({
  ...result,
  id: result.AppStoreId,
  name: result.AppTitle,
}));

export const useAppStoreRepository = ({
  platform,
}) => {
  const serverContext = useContext(ServerContext);
  const acid = serverContext.CurrentAccount.AdvertiserCustomerID;

  const { scope } = useContext(ScopeContext) || {};

  const { aid, cid } = scope.values;

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const searchAppManual = useCallback((searchQuery, country) => {
    const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/SearchApp`;

    const url = queryify(baseURL, {
      $top: 15,
      $skip: 0,
      $search: searchQuery,
      $filter: `AppPlatform eq '${platform}'`,
      countryCode: country,
    });

    return odata.get(url, oDataConfig)
      .then(response => attachIdsToResults(response.value));
  }, [aid, cid, oDataConfig, platform]);

  const searchApp = useCallback((searchQuery) => {
    if (platform !== platforms.WINDOWS) {
      const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Apps`;

      const url = queryify(baseURL, {
        $top: 15,
        $skip: 0,
        $search: searchQuery,
        $filter: `AppPlatform eq '${platform}'`,
      });

      return odata.get(url, oDataConfig)
        .then(response => attachIdsToResults(response.value));
    }

    return publisherQueries.getMatchedPublishers(acid, searchQuery, 'Application')
      .then((response) => {
        const items = response.MatchingAppications;

        const newMenuItems = items.map(app => ({
          AppId: app.AppId,
          AppStoreId: app.AppId,
          AppTitle: app.AppTitle,
          IconUrl: app.ImageLink,
          DeveloperName: app.PublisherName,
          AppPlatform: 'Windows',
        }));

        return attachIdsToResults(newMenuItems);
      });
  }, [aid, cid, acid, oDataConfig, platform]);

  const searchAppById = useCallback(id => searchApp(id).then(results => results[0]), [searchApp]);

  return {
    searchApp,
    searchAppManual,
    searchAppById,
    oDataConfig,
  };
};
